/**
 * scopeMapping.js — Draft model, helpers, and API ↔ draft mapping
 * for the grouped scope builder (DNF: AND within groups, OR between groups).
 *
 * Draft model (frontend-only, never sent to API directly):
 *
 *   ScopeDraft        { total?: boolean, groups: ScopeGroupDraft[] }
 *   ScopeGroupDraft   { id: string, clauses: ScopeClauseDraft[] }
 *   ScopeClauseDraft  { id: string, dimension_id: number|null,
 *                        operator: string, node_id: number|null,
 *                        node_label: string|null }
 */

// ── Factory helpers ─────────────────────────────────────────────

let _counter = 0;
const uid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / test environments
  _counter += 1;
  return `draft-${Date.now()}-${_counter}`;
};

/**
 * Create a blank clause ready for editing.
 */
export const createEmptyClause = () => ({
  id: uid(),
  dimension_id: null,
  operator: "equals",
  node_id: null,
  node_label: null,
});

/**
 * Create a new group pre-populated with one empty clause.
 */
export const createEmptyGroup = () => ({
  id: uid(),
  clauses: [createEmptyClause()],
});

// ── Validation helpers ──────────────────────────────────────────

/**
 * A clause is complete when it has a valid dimension and a value selected.
 */
export const isClauseComplete = (clause) =>
  clause.dimension_id != null &&
  clause.dimension_id > 0 &&
  clause.node_id != null &&
  clause.node_id > 0;

/**
 * A group is valid when it has at least one clause and all clauses are complete.
 */
export const isGroupValid = (group) =>
  group.clauses.length > 0 && group.clauses.every(isClauseComplete);

/**
 * The entire scope draft is valid when there is at least one group
 * and every group is valid.
 */
export const isScopeValid = (draft) =>
  draft.groups.length > 0 && draft.groups.every(isGroupValid);

// ── Finding the first invalid clause (for validation UX) ────────

/**
 * Returns { groupId, clauseId } of the first incomplete clause,
 * or null if the draft is fully valid.
 */
export const findFirstInvalid = (draft) => {
  for (const group of draft.groups) {
    for (const clause of group.clauses) {
      if (!isClauseComplete(clause)) {
        return { groupId: group.id, clauseId: clause.id };
      }
    }
  }
  return null;
};

// ── API ↔ Draft mapping ─────────────────────────────────────────

/**
 * Convert an API scope response (flat clauses with clause_group int)
 * into the draft model used by the scope sheet.
 *
 * API shape:
 *   { scope_mode: "include", clauses: [ { clause_group, dimension_id, operator, node_id, node_label }, ... ] }
 *
 * Returns:
 *   ScopeDraft { groups: ScopeGroupDraft[] }
 */
export const apiToDraft = (apiScope) => {
  // Total scope: covers all cloud & AI spend, no clauses.
  if (apiScope?.scope_mode === "total") {
    return { total: true, groups: [] };
  }

  const clauses = Array.isArray(apiScope?.clauses) ? apiScope.clauses : [];

  if (clauses.length === 0) {
    return { groups: [] };
  }

  // Group flat clauses by clause_group (integer key)
  const byGroup = {};
  for (const c of clauses) {
    const key = c.clause_group ?? 1;
    if (!byGroup[key]) byGroup[key] = [];
    byGroup[key].push(c);
  }

  // Sort group keys numerically and build draft groups
  const sortedKeys = Object.keys(byGroup)
    .map(Number)
    .sort((a, b) => a - b);

  const groups = sortedKeys.map((key) => ({
    id: uid(),
    clauses: byGroup[key].map((c) => ({
      id: uid(),
      dimension_id: c.dimension_id ?? null,
      operator: c.operator ?? "equals",
      node_id: c.node_id ?? null,
      node_label: c.node_label ?? null,
    })),
  }));

  return { groups };
};

/**
 * Convert a draft model back to the flat API payload.
 *
 * Each group gets a sequential clause_group integer (1-based).
 * Draft-only fields (id) are stripped.
 *
 * Returns:
 *   { scope_mode: string, clauses: [ { clause_group, dimension_id, operator, node_id }, ... ] }
 */
export const draftToApi = (draft, scopeMode = "include") => {
  // Total scope: covers all cloud & AI spend, regardless of any leftover groups.
  if (draft?.total) {
    return { scope_mode: "total", clauses: [] };
  }

  const clauses = [];
  draft.groups.forEach((group, groupIdx) => {
    const clauseGroup = groupIdx + 1;
    for (const c of group.clauses) {
      // Only include complete clauses
      if (isClauseComplete(c)) {
        clauses.push({
          clause_group: clauseGroup,
          dimension_id: c.dimension_id,
          operator: c.operator,
          node_id: c.node_id,
          node_label: c.node_label ?? null,
        });
      }
    }
  });

  return {
    scope_mode: scopeMode,
    clauses,
  };
};
